<template>
    <div style="width: 100%; height: 250px;">
        <canvas ref="chartCanvas"></canvas>
    </div>
</template>

<script setup>
    import { ref, onMounted, watch, onBeforeUnmount } from "vue";
    import { Chart } from "chart.js";
    import { eventBus } from '../core/EventBus';

    const props = defineProps({
        dadosGrafico: Object,
    });

    const chartCanvas = ref(null);
    let chartInstance = null;

    const criarDataSet = (label, data) => {
        return {
            label: label,
            data: data,
            backgroundColor: "#1f64e5",
            borderRadius: 0,
            barPercentage: 1,
            categoryPercentage: 0.5,
        };
    };

    const atualizarDadosGrafico = (dados) => {
        if (!chartInstance || !dados || !dados.dadosX || !dados.dadosY) return;

        chartInstance.data.labels = dados.dadosX;
        chartInstance.data.datasets = dados.dadosY.map(dadoY =>
            criarDataSet(dadoY.label, dadoY.data)
        );
        chartInstance.update();
    };

    const criarGrafico = () => {
        if (chartInstance) {
            chartInstance.destroy();
        }

        if (chartCanvas.value) {
            let backgroundColor = ['#ffffff', '#ffffff'];
            let textColor = "#000000";

            if (localStorage.getItem("theme") === 'dark') {
                backgroundColor = ['#101720', '#17212E'];
                textColor = "#ffffff";
            }

            const plugin = {
                id: 'customCanvasBackgroundColor',
                beforeDraw: (chart) => {
                    const { ctx, width, height } = chart;

                    const gradient = ctx.createLinearGradient(0, height, 0, 0);
                    gradient.addColorStop(0, backgroundColor[0]);
                    gradient.addColorStop(1, backgroundColor[1]);

                    const radius = 5;

                    ctx.save();
                    ctx.globalCompositeOperation = 'destination-over';
                    ctx.fillStyle = gradient;

                    const x = 0;
                    const y = 0;
                    const w = width * 2;
                    const h = height * 2;

                    ctx.beginPath();
                    ctx.moveTo(x + radius, y);
                    ctx.lineTo(x + w - radius, y);
                    ctx.quadraticCurveTo(x + w, y, x + w, y + radius);
                    ctx.lineTo(x + w, y + h - radius);
                    ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h);
                    ctx.lineTo(x + radius, y + h);
                    ctx.quadraticCurveTo(x, y + h, x, y + h - radius);
                    ctx.lineTo(x, y + radius);
                    ctx.quadraticCurveTo(x, y, x + radius, y);
                    ctx.closePath();
                    ctx.fill();

                    ctx.restore();
                }
            };

            const kWhLabelPlugin = {
                id: 'kWhLabelPlugin',
                afterDatasetsDraw: (chart) => {
                    const { ctx, chartArea } = chart;

                    ctx.save();
                    ctx.fillStyle = textColor;
                    ctx.textAlign = "center";
                    ctx.textBaseline = "middle";

                    const x = chartArea.left - 55;
                    const y = (chartArea.top + chartArea.bottom) / 2;

                    ctx.fillText("kWh", x, y);
                    ctx.restore();
                }
            };

            chartInstance = new Chart(chartCanvas.value, {
                type: "bar",
                data: {
                    labels: [],
                    datasets: [],
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    layout: {
                        padding: {
                            left: 40,
                            top: 15,
                            right: 15
                        }
                    },
                    plugins: {
                        legend: {
                            position: "bottom",
                            labels: {
                                color: textColor,
                                usePointStyle: true,
                                pointStyle: "rect",
                            },
                        },
                    },
                    scales: {
                        y: {
                            beginAtZero: true,
                            grid: {
                                color: "#2c3e50",
                            },
                            ticks: {
                                color: textColor,
                            },
                        },
                        x: {
                            grid: {
                                color: "#2c3e50",
                            },
                            ticks: {
                                color: textColor,
                                maxRotation: 45,
                                minRotation: 45,
                            },
                        },
                    },
                },
                plugins: [plugin, kWhLabelPlugin],
            });
        }
    };

    const objetosSaoIguais = (a, b) => {
        return JSON.stringify(a) === JSON.stringify(b);
    };

    let mudouTemaHandler;

    onMounted(() => {
        criarGrafico();
        atualizarDadosGrafico(props.dadosGrafico);

        mudouTemaHandler = () => {
            criarGrafico();
            atualizarDadosGrafico(props.dadosGrafico);
        };

        eventBus.on("mudou-tema", mudouTemaHandler);
    });

    watch(() => props.dadosGrafico, (newData, oldData) => {
        if (!objetosSaoIguais(newData, oldData)) {
            atualizarDadosGrafico(newData);
        }
    }, { deep: true });

    onBeforeUnmount(() => {
        if (chartInstance) {
            chartInstance.destroy();
        }

        if (mudouTemaHandler) {
            eventBus.off("mudou-tema", mudouTemaHandler);
        }
    });

</script>
